// Google Places (New) API helpers, ported from the onboarding flow at
// web-partner/src/pages/Sign/Company/Company.js. The edit page only needs
// search + place-details; merchant onboarding intake stays the source of
// truth for the original `companyGoogle` / `companyGooglePlaces` blobs.

const PLACES_BASE = 'https://places.googleapis.com/v1/places';

// Same `includedPrimaryTypes` set used by onboarding so admin search results
// match what merchants saw at signup.
const INCLUDED_PRIMARY_TYPES = [
  'accounting',
  'insurance_agency',
  'establishment',
  'travel_agency',
  'real_estate_agency',
];

// Map Google Place `types` → SKAi1 sector slug (parent category). Mirrors the
// classification used in onboarding so an edit re-sync resolves to the same
// sector the merchant originally got.
const SECTOR_RULES = [
  {
    slug: 'food-beverage',
    types: [
      'restaurant',
      'food',
      'bakery',
      'cafe',
      'meal_delivery',
      'meal_takeaway',
      'bar',
      'night_club',
    ],
  },
  {
    slug: 'entertainment',
    types: [
      'amusement_park',
      'movie_theater',
      'casino',
      'museum',
      'art_gallery',
      'tourist_attraction',
      'zoo',
      'aquarium',
      'bowling_alley',
      'stadium',
    ],
  },
  {
    slug: 'travel',
    types: [
      'lodging',
      'hotel',
      'travel_agency',
      'airport',
      'bus_station',
      'subway_station',
      'train_station',
      'transit_station',
      'car_rental',
    ],
  },
  {
    slug: 'sports',
    types: ['gym', 'stadium', 'bicycle_store', 'sporting_goods_store'],
  },
  { slug: 'pets', types: ['pet_store', 'veterinary_care'] },
  {
    slug: 'lifestyle',
    types: [
      'beauty_salon',
      'hair_care',
      'spa',
      'clothing_store',
      'department_store',
      'jewelry_store',
      'shopping_mall',
      'store',
      'furniture_store',
      'home_goods_store',
    ],
  },
];

const apiKey = () => process.env.REACT_APP_GCP_API_KEY;

export const hasGoogleApiKey = () => !!apiKey();

export const resolveSectorSlug = placeTypes => {
  const types = Array.isArray(placeTypes) ? placeTypes : [];
  for (const rule of SECTOR_RULES) {
    if (rule.types.some(t => types.includes(t))) return rule.slug;
  }
  return 'other';
};

/**
 * Autocomplete search. Returns array of `{ placeId, label }`. Empty array
 * when API key missing or no results — caller decides UI for the empty state.
 */
export const searchPlaces = async query => {
  const key = apiKey();
  if (!key || !query || !query.trim()) return [];
  const res = await fetch(`${PLACES_BASE}:autocomplete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': key,
      'X-Goog-FieldMask':
        'suggestions.placePrediction.placeId,suggestions.placePrediction.structuredFormat.mainText.text,suggestions.placePrediction.structuredFormat.secondaryText.text,suggestions.placePrediction.text.text',
    },
    body: JSON.stringify({
      input: query,
      includedPrimaryTypes: INCLUDED_PRIMARY_TYPES,
      locationBias: {
        rectangle: {
          low: { latitude: -90, longitude: -180 },
          high: { latitude: 90, longitude: 180 },
        },
      },
    }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
  return suggestions
    .filter(s => s.placePrediction)
    .map(s => {
      const p = s.placePrediction;
      const main =
        p.structuredFormat?.mainText?.text || p.text?.text || '';
      const secondary = p.structuredFormat?.secondaryText?.text || '';
      return {
        placeId: p.placeId,
        label: secondary ? `${main}, ${secondary}` : main,
      };
    });
};

/**
 * Fetch detailed place data. Throws on network/auth error so callers can
 * surface a toast; returns raw payload otherwise (full blob preserved for
 * `companyGooglePlaces` audit field).
 */
export const fetchPlaceDetails = async placeId => {
  const key = apiKey();
  if (!key) throw new Error('Google Places API key missing');
  if (!placeId) throw new Error('placeId is required');
  const res = await fetch(`${PLACES_BASE}/${encodeURIComponent(placeId)}`, {
    method: 'GET',
    headers: {
      'X-Goog-Api-Key': key,
      'X-Goog-FieldMask':
        'displayName,formattedAddress,addressComponents,types,internationalPhoneNumber,rating,location',
    },
  });
  if (!res.ok) throw new Error(`Place details fetch failed: ${res.status}`);
  return res.json();
};

/**
 * Derive form-shaped values from a place details response. Returned shape
 * matches the form keys used in MerchantEditBusinessDetailsPage so the caller
 * can spread it directly.
 */
export const extractFormFromPlace = (place, sectors = []) => {
  if (!place) return {};
  const addressComponents = Array.isArray(place.addressComponents)
    ? place.addressComponents
    : [];
  const country = addressComponents.find(c =>
    Array.isArray(c.types) && c.types.includes('country')
  );
  const postalCode = addressComponents.find(c =>
    Array.isArray(c.types) && c.types.includes('postal_code')
  );

  const sectorSlug = resolveSectorSlug(place.types);
  // Sectors fetched from BE expose `name` (label) but not `slug`. Best-effort
  // map: prefer slug match if BE eventually adds it, else use a name match
  // that loosely follows the slug (e.g. "food-beverage" → "Food & Beverage").
  const sectorMatch = sectors.find(s => {
    if (!s) return false;
    if (s.slug && s.slug === sectorSlug) return true;
    const normalized = String(s.name || '')
      .toLowerCase()
      .replace(/&/g, '')
      .replace(/\s+/g, '-');
    return normalized === sectorSlug;
  });

  // Phone: BE wants full E.164. internationalPhoneNumber is already a
  // space-separated E.164 string, just strip whitespace.
  const phone = place.internationalPhoneNumber
    ? String(place.internationalPhoneNumber).replace(/\s+/g, '')
    : '';

  return {
    companyName: place.displayName?.text || '',
    companyAddress: place.formattedAddress || '',
    companyZip:
      postalCode?.shortText || postalCode?.longText || '',
    companyCountry: country?.longText || country?.text || '',
    companyCountryCode: country?.shortText || '',
    companyLat:
      place.location?.latitude !== undefined
        ? String(place.location.latitude)
        : '',
    companyLon:
      place.location?.longitude !== undefined
        ? String(place.location.longitude)
        : '',
    companyPhone: phone,
    companyCategory: sectorMatch?.name || '',
    companyCategoryId: sectorMatch?.id || '',
  };
};
